import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Environment, ENVIRONMENT, IpsecProposal } from '@netfoundry-ui/shared/model';
import { HTTP_CLIENT } from '@netfoundry-ui/shared/services';
import { Observable } from 'rxjs';
import {
    HateoasResourceOperation,
    PagedGetOption,
    PagedResourceCollection,
} from '@lagoshny/ngx-hateoas-client';

/**
 * IpsecProposalServiceV2
 *
 * CloudZiti V8 service for the IpsecProposal catalog entity. Read-only from
 * the UI's perspective — the catalog is server-managed.
 *
 * Like IpsecTunnelerServiceV2, the legacy `*V2` suffix is kept for
 * consistency with the HateoasResourceOperation typing — the runtime URL
 * resolves to environment.v3apiUrl (`/core/v3/...`) when v3Enabled is true.
 */
@Injectable({ providedIn: 'root' })
export class IpsecProposalServiceV2 extends HateoasResourceOperation<IpsecProposal> {
    static defaultHttpAccept = {
        headers: { accept: 'application/hal+json' },
        pageParams: {
            page: 0,
            size: 100,
        },
    };
    apiUrl: string;

    constructor(
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {
        super(IpsecProposal);
        this.apiUrl = environment.v3Enabled ? environment.v3apiUrl : environment.v2apiUrl;
    }

    override getResource(): Observable<IpsecProposal> {
        throw new Error('Do not use: see getIkeProposals / getEspProposals');
    }

    override getPage(): Observable<PagedResourceCollection<IpsecProposal>> {
        throw new Error('Do not use: see getIkeProposals / getEspProposals');
    }

    /**
     * GET /v3/ipsec-proposals?type=IKE
     * Returns the IKE proposal catalog, sorted by `sortOrder` ASC.
     */
    getIkeProposals(): Promise<IpsecProposal[]> {
        return this.fetchByType('IKE');
    }

    /**
     * GET /v3/ipsec-proposals?type=ESP
     * Returns the ESP proposal catalog, sorted by `sortOrder` ASC.
     */
    getEspProposals(): Promise<IpsecProposal[]> {
        return this.fetchByType('ESP');
    }

    private fetchByType(type: 'IKE' | 'ESP'): Promise<IpsecProposal[]> {
        const options: PagedGetOption = {
            ...IpsecProposalServiceV2.defaultHttpAccept,
            params: { type },
        };
        return super
            .getPage(options)
            .toPromise()
            .then((page) => {
                const resources = page ? page.resources : [];
                return resources.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
            });
    }
}
